import * as React from 'react';

/**
 * Preferência de tema claro/escuro, persistida em localStorage (chave
 * `pan.tema-escuro`) pra sobreviver a F5 e novas abas. Sem valor salvo, cai
 * pro `prefers-color-scheme` do sistema operacional.
 */

const CHAVE_STORAGE = 'pan.tema-escuro';

function lerPreferenciaSalva(): boolean | null {
  try {
    const bruto = localStorage.getItem(CHAVE_STORAGE);
    if (bruto === 'true') return true;
    if (bruto === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function salvarPreferencia(valor: boolean): void {
  try {
    localStorage.setItem(CHAVE_STORAGE, String(valor));
  } catch {
    // Storage bloqueado (aba anônima com política restrita, etc.) — a
    // preferência simplesmente não sobrevive a um F5, sem quebrar o resto.
  }
}

function preferenciaInicial(): boolean {
  const salva = lerPreferenciaSalva();
  if (salva !== null) return salva;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

export function useTemaEscuro(): [boolean, (valor: boolean | ((atual: boolean) => boolean)) => void] {
  const [escuro, setEscuroState] = React.useState(preferenciaInicial);

  const setEscuro = React.useCallback((valor: boolean | ((atual: boolean) => boolean)) => {
    setEscuroState((atual) => {
      const novo = typeof valor === 'function' ? valor(atual) : valor;
      salvarPreferencia(novo);
      return novo;
    });
  }, []);

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', escuro);
  }, [escuro]);

  return [escuro, setEscuro];
}
